/**
 * Экран входа.
 *
 * Второй фактор запрашивается на том же экране, а не на отдельном: пользователь
 * уже ввёл пароль, и переход на другую страницу читается как ошибка.
 *
 * Перед формой идёт заставка. Форма при этом всё время в разметке и держит
 * фокус: заставка ничего не задерживает, а первое же нажатие клавиши обрывает
 * её, и буква попадает в поле.
 */

import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { LoginIntro, introWanted, rememberIntroShown } from "@/modules/auth/LoginIntro";
import * as api from "@/shared/api/auth";
import { ApiError, setAccessToken } from "@/shared/api/client";
import { cn } from "@/shared/lib/cn";
import { useSession } from "@/shared/lib/session";
import { Button } from "@/shared/ui/Button";
import { InlineAlert } from "@/shared/ui/Feedback";
import { TextField } from "@/shared/ui/TextField";

const schema = z.object({
  email: z.string().min(1, "Укажите адрес").email("Похоже, адрес указан с ошибкой"),
  password: z.string().min(1, "Введите пароль"),
  totpCode: z.string().optional(),
  accountCode: z.string().optional(),
});

/** Сервер обслуживает несколько контуров: тогда одного адреса для входа мало. */
const ACCOUNT_REQUIRED = "account_required";

type FormValues = z.infer<typeof schema>;

export function LoginPage(): React.ReactElement {
  const signIn = useSession((state) => state.signIn);
  const awaitingSecondFactor = useSession((state) => state.awaitingSecondFactor);
  const restore = useSession((state) => state.restore);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [leaving, setLeaving] = React.useState(false);

  // Спрашивается до того, как человек назвался: наружу отдаётся только
  // надпись на кнопке, адрес провайдера неизвестному знать незачем
  const sso = useQuery({ queryKey: ["sso-offer"], queryFn: api.ssoOffer, retry: false });

  async function toProvider(): Promise<void> {
    setLeaving(true);
    try {
      const started = await api.ssoStart(`${window.location.origin}/login`);
      window.location.assign(started.url);
    } catch (error) {
      setLeaving(false);
      setFormError(error instanceof Error ? error.message : "Провайдер входа недоступен");
    }
  }
  const [accountNeeded, setAccountNeeded] = React.useState(false);

  // Возврат от провайдера: он приводит человека обратно с кодом в адресе.
  // Код одноразовый, поэтому обмен идёт ровно один раз, а адрес сразу
  // очищается — обновление страницы не должно пытаться обменять его снова
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (code === null || state === null) {
      return;
    }
    window.history.replaceState(null, "", window.location.pathname);
    setLeaving(true);
    void api
      .ssoFinish(code, state)
      .then(async (issued) => {
        setAccessToken(issued.access_token);
        await restore();
      })
      .catch((error: unknown) => {
        setLeaving(false);
        setFormError(error instanceof ApiError ? error.message : "Провайдер не подтвердил вход");
      });
  }, [restore]);

  const formRef = React.useRef<HTMLFormElement>(null);
  // Решение принимается один раз при монтировании: неудачный вход не должен
  // проигрывать заставку заново
  const [intro] = React.useState(introWanted);
  const [shown, setShown] = React.useState(!intro);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "", totpCode: "", accountCode: "" },
  });

  async function onSubmit(values: FormValues): Promise<void> {
    setFormError(null);
    try {
      await signIn(
        values.email,
        values.password,
        values.totpCode || undefined,
        values.accountCode || undefined,
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === ACCOUNT_REQUIRED) {
        // Поле появляется только когда оно действительно нужно: на сервере
        // с одним контуром спрашивать его код бессмысленно
        setAccountNeeded(true);
      }
      setFormError(
        error instanceof ApiError ? error.message : "Не удалось войти. Повторите попытку",
      );
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--pt-bg-canvas)] p-[var(--pt-space-24)]">
      {/* Снимает себя заставка сама, когда догорит контур. Если снимать её
          по появлению формы, последние полсекунды — то самое проявление формы
          внутри собственного очертания — не показываются вовсе */}
      {intro && (
        <LoginIntro
          target={formRef}
          onReveal={() => {
            setShown(true);
            rememberIntroShown();
            // Подстраховка: если автофокус не сработал или его перехватили,
            // человек всё равно может начать печатать сразу
            const form = formRef.current;
            if (form !== null && !form.contains(document.activeElement)) {
              form.querySelector<HTMLInputElement>("input")?.focus();
            }
          }}
        />
      )}
      <form
        ref={formRef}
        onSubmit={handleSubmit(onSubmit)}
        className={cn(
          "w-[360px] rounded-[var(--pt-radius-8)] border border-[var(--pt-border-default)]",
          "bg-[var(--pt-bg-surface)] p-[var(--pt-space-24)] shadow-[var(--pt-shadow-2)]",
          // Форма занимает место с самого начала: заставке нужно её очертание,
          // а полю ввода — фокус. Скрыта прозрачностью, а не видимостью:
          // в скрытом по visibility поддереве браузер фокус не ставит,
          // и первая набранная буква пропала бы
          shown ? "pt-reveal" : "opacity-0",
        )}
        noValidate
      >
        <h1 className="text-[var(--pt-fs-xl)] leading-[var(--pt-lh-xl)] font-semibold text-[var(--pt-text-primary)]">
          PlanTask
        </h1>
        <p className="mt-[var(--pt-space-4)] mb-[var(--pt-space-20)] text-[var(--pt-fs-sm)] text-[var(--pt-text-secondary)]">
          Вход в систему управления работой
        </p>

        {formError && (
          <InlineAlert tone="danger" className="mb-[var(--pt-space-12)]">
            {formError}
          </InlineAlert>
        )}

        <TextField
          label="Рабочая почта"
          type="email"
          autoComplete="username"
          autoFocus
          reserveErrorSpace
          error={errors.email?.message}
          {...register("email")}
        />

        <TextField
          label="Пароль"
          type="password"
          autoComplete="current-password"
          reserveErrorSpace
          error={errors.password?.message}
          {...register("password")}
        />

        {accountNeeded && (
          <TextField
            label="Код контура"
            hint="Сервер обслуживает несколько контуров — укажите свой"
            containerClassName="mt-[var(--pt-space-12)]"
            error={errors.accountCode?.message}
            {...register("accountCode")}
          />
        )}

        {awaitingSecondFactor && (
          <TextField
            label="Код подтверждения"
            hint="Шесть цифр из приложения-аутентификатора или код восстановления"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            error={errors.totpCode?.message}
            {...register("totpCode")}
          />
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={isSubmitting}
          className="mt-[var(--pt-space-8)] w-full"
        >
          {awaitingSecondFactor ? "Подтвердить вход" : "Войти"}
        </Button>

        {/* Кнопка провайдера появляется, только если он настроен. Форма с
            паролем остаётся всегда: местная учётная запись должна работать
            и тогда, когда провайдер или каталог недоступны */}
        {sso.data?.is_enabled === true && !awaitingSecondFactor && (
          <>
            <p className="my-[var(--pt-space-12)] text-center text-[var(--pt-fs-xs)] text-[var(--pt-text-tertiary)]">
              или
            </p>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full"
              loading={leaving}
              onClick={() => void toProvider()}
            >
              {sso.data.button_label}
            </Button>
          </>
        )}
      </form>
    </div>
  );
}
